import { AlertTriangle, Loader } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactElement, type ReactNode, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { gitAuthUrls } from '@/config/git-auth-urls'
import { BitBucketLogo, GitHubLogo, GitLabLogo } from '@/logos'
import type { GitType } from '@/graphql.types'

type Inputs = {
  email: string
  password: string
}

const state = encodeURIComponent(
  JSON.stringify({
    action: 'login',
    api: import.meta.env.VITE_GRAPHQL_SERVER_URI,
    redirectUri: window.location.origin + '/login',
  })
)

const GIT_PROVIDERS: Array<{
  name: string
  gitType: GitType
  icon: ReactNode
  authUrl: string
}> = [
  {
    name: 'GitHub',
    gitType: 'github',
    icon: <GitHubLogo />,
    authUrl: `${gitAuthUrls.github}&state=${state}`,
  },
  {
    name: 'GitLab',
    gitType: 'gitlab',
    icon: <GitLabLogo />,
    authUrl: `${gitAuthUrls.gitlab}&state=${state}`,
  },
  {
    name: 'Bitbucket',
    gitType: 'bitbucket',
    icon: <BitBucketLogo />,
    authUrl: `${gitAuthUrls.bitbucket}&state=${state}`,
  },
]

export function LoginForm({
  isPending,
  onSubmit,
  error,
}: {
  error: string | ReactElement | undefined
  isPending: boolean
  onSubmit: (data: Inputs) => void
}) {
  const [gitLoading, setGitLoading] = useState<GitType | null>(null)
  const form = useForm<Inputs>({
    defaultValues: {
      email: '',
      password: '',
    },
  })
  const { handleSubmit } = form
  const loadingGitProvider = GIT_PROVIDERS.find(
    ({ gitType }) => gitType === gitLoading
  )
  const isFormBusy = isPending || Boolean(loadingGitProvider)
  const statusMessage = isPending
    ? 'Logging in...'
    : loadingGitProvider
      ? `Redirecting to ${loadingGitProvider.name}...`
      : ''

  return (
    <>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} aria-busy={isFormBusy}>
          <div className="grid gap-6">
            <div
              className="sr-only"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {statusMessage}
            </div>
            <div className="grid grid-cols-3 gap-4">
              {GIT_PROVIDERS.map((git) => (
                <Button
                  key={git.gitType}
                  aria-label={
                    gitLoading === git.gitType
                      ? `Redirecting to ${git.name}...`
                      : `Continue with ${git.name}`
                  }
                  variant="outline"
                  disabled={Boolean(gitLoading)}
                  type="button"
                  onClick={() => {
                    setGitLoading(git.gitType)
                    window.location.href = git.authUrl
                  }}
                >
                  {gitLoading === git.gitType ? (
                    <Loader className="animate-spin" aria-hidden="true" />
                  ) : (
                    git.icon
                  )}
                  {git.name}
                </Button>
              ))}
            </div>
            <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
              <span className="bg-card text-muted-foreground relative z-10 px-2">
                Or continue with
              </span>
            </div>
            <div className="grid gap-6">
              {error ? (
                <AnimatePresence>
                  <motion.div
                    key="alert"
                    layout
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Alert variant="destructive" className="mx-auto w-full">
                      <AlertTriangle />
                      <AlertTitle>Authentication error</AlertTitle>
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  </motion.div>
                </AnimatePresence>
              ) : null}
              <FormField
                control={form.control}
                name="email"
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                }}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        tabIndex={1}
                        disabled={isPending}
                        autoComplete="email"
                        placeholder="my.email@example.com"
                        aria-label="Email"
                        value={value}
                        onChange={onChange}
                        type="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                rules={{
                  required: 'Password is required',
                }}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <FormLabel>Password</FormLabel>
                      <Link
                        className="ml-auto text-sm underline-offset-4 hover:underline"
                        to="/login/request-reset-password"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                    <FormControl>
                      <Input
                        tabIndex={2}
                        value={value}
                        onChange={onChange}
                        autoComplete="current-password"
                        aria-label="Password"
                        disabled={isPending}
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                tabIndex={3}
                type="submit"
                className="w-full"
                aria-label={isPending ? 'Logging in...' : 'Login'}
                disabled={isPending}
              >
                Login
              </Button>
            </div>
            <div className="flex flex-col gap-4">
              <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                <span className="bg-card text-muted-foreground relative z-10 px-2">
                  Or
                </span>
              </div>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/login/sso">Sign in with SSO</Link>
              </Button>
            </div>
            <div className="text-center text-sm">
              Don&apos;t have an account?{' '}
              <a
                href="https://sevalla.com/signup/"
                className="underline underline-offset-4"
              >
                Sign up
              </a>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
